import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, EQUIPMENT_CATEGORIES } from "@/data/content";
import { Reveal, ChapterLabel, PageHero, ParallaxImage, ImageReveal, usePageTitle } from "@/components/Shared";

const EquipmentPage = () => {
  const { t, lf } = useLang();
  usePageTitle(`${t.nav.equipment} — ${ARTIST_NAME}`);

  return (
    <div data-testid="equipment-page">
      <PageHero eyebrow={t.equipment.eyebrow} title={t.equipment.title} img={IMG.saxDetail}>
        <Reveal delay={0.4}>
          <p className="font-sans text-sm leading-relaxed text-[#A1A1AA]">{t.equipment.intro}</p>
        </Reveal>
      </PageHero>

      <section className="px-6 py-20 sm:px-12 sm:py-28 lg:px-20">
        <div className="space-y-28">
          {EQUIPMENT_CATEGORIES.map((cat, i) => (
            <div key={cat.id} className="grid items-start gap-10 lg:grid-cols-12 lg:gap-16">
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div data-cursor="VIEW">
                  <ImageReveal src={cat.img} alt={lf(cat.label)} bw={i % 2 === 1} delay={0.05} className="aspect-[4/3]">
                    <div className="absolute inset-0 bg-[#0B0B0C]/20" />
                    <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#F9F8F6]/70">
                      {String(i + 1).padStart(2, "0")} / {lf(cat.label)}
                    </span>
                  </ImageReveal>
                </div>
              </div>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : "lg:col-start-7"}`}>
                <Reveal>
                  <ChapterLabel no={String(i + 1).padStart(2, "0")} label={lf(cat.label)} />
                </Reveal>
                <div className="space-y-10">
                  {cat.items.map((item, j) => (
                    <Reveal key={j} delay={0.1 + j * 0.08}>
                      <article data-testid="equipment-item-card" className="border-l border-[#C5A059]/30 pl-6">
                        <h3 className="font-serif text-2xl font-light text-[#F9F8F6] sm:text-3xl">{item.name}</h3>
                        <p className="mb-2 mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.equipment.whyLabel}</p>
                        <p className="max-w-lg font-sans text-sm leading-relaxed text-[#A1A1AA]">{lf(item.why)}</p>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Live / Studio Setup */}
      <section className="border-t border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal><ChapterLabel no="A" label={t.equipment.liveSetupLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="font-sans text-sm leading-loose text-[#A1A1AA]">{t.equipment.liveSetup}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8">
              <ParallaxImage src={IMG.mixer} alt="Live Setup" className="aspect-[16/9]" />
            </Reveal>
          </div>
          <div className="lg:mt-24">
            <Reveal><ChapterLabel no="B" label={t.equipment.studioSetupLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="font-sans text-sm leading-loose text-[#A1A1AA]">{t.equipment.studioSetup}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8">
              <ParallaxImage src={IMG.studioMic} alt="Studio Setup" className="aspect-[16/9]" />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquipmentPage;
